import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Button,
    TextField,
    CircularProgress,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    PaperProps,
    Tab,
    Box
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDataProvider, useRefresh, useNotify } from "react-admin";
import { useForm, useWatch } from "react-hook-form";
import ApiResources from "src/providers/ApiResources";
import Draggable from 'react-draggable';
import { SelectField, InputSeparator } from "src/components/input";
import { CountryEnum, StateEnum, StatusEnum } from "src/common/enum";
import { AddressFormAutocomplete } from "src/components/address/AddressFormAutocomplete";
import { AddressFormTelInput } from "src/components/address/AddressFormTelInput";

interface GroupCreateModalProps {
    open: boolean;
    setOpen: any;
    onSubmit?: Function;
    defaultValues?: any;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}
  
export const GroupCreateModal = (props: GroupCreateModalProps) => {
    const { open, setOpen, onSubmit = () => {}, defaultValues = {} } = props;

    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const { handleSubmit, register, control, getValues, setValue, formState: { errors }} = useForm({
        defaultValues: defaultValues
    });
    const country = useWatch({control, name: 'country'});

    useEffect(() => {
      setValue('state', '');
    }, [country]);

    const createGroup = async (data) => {
        try {
            let formData = {...data};

            setLoading(true);

            dataProvider
            .create(ApiResources.getGroups(false), {data: formData})
            .then((res) => {
                notify('Group has been created succesfully.');
                setLoading(false);
                onSubmit(res.data);
                setOpen(false);
            })
            .catch((error) => {
                setLoading(false);
                notify(error.message, { type: 'warning' });
            });
        } catch (e) {
            console.log(e);
        }
    }


    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(createGroup)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>Create Group</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container rowSpacing={1} columnSpacing={2}>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Name"
                            variant="outlined"
                            size="small"
                            error={!!errors.name}
                            helperText={(errors.name as any)?.message}
                            {...register("name", {
                                required: "Field is required"
                            })}
                            onChange={(e) => setValue("name", e.target?.value, { shouldValidate: true })}
                            required
                            value={getValues("name") || ""}
                        /> 
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            size="small"
                            error={!!errors.email}
                            helperText={(errors.email as any)?.message}
                            {...register("email", {
                                required: "Field is required"
                            })}
                            onChange={(e) => setValue("email", e.target?.value, { shouldValidate: true })}
                            required
                            value={getValues("email") || ""}
                        /> 
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <AddressFormTelInput
                            label="Phone"
                            error={!!errors.phone}
                            helperText={(errors.phone as any)?.message}
                            {...register("phone", {
                                required: "Field is required"
                            })}
                            onChange={(value) => setValue("phone", value, { shouldValidate: true })}
                            required
                            value={getValues("phone") || ""}
                        />
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <AddressFormTelInput
                            label="Mobile"
                            error={!!errors.mobile}
                            helperText={(errors.mobile as any)?.message}
                            {...register("mobile")}
                            onChange={(value) => setValue("mobile", value, { shouldValidate: true })}
                            value={getValues("mobile") || ""}
                        />
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Website"
                            variant="outlined"
                            size="small"
                            error={!!errors.website}
                            helperText={(errors.website as any)?.message}
                            {...register("website")}
                            onChange={(e) => setValue("website", e.target?.value, { shouldValidate: true })}
                            value={getValues("website") || ""}
                        /> 
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Status"
                            size="small"
                            value={getValues("status") || ""}
                            error={!!errors.status}
                            helperText={(errors.status as any)?.message}
                            required
                            choices={StatusEnum}
                            {...register("status", {
                                required: "Field is required"
                            })}
                            onChange={(e) => {
                                setValue('status', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Resale Number"
                            variant="outlined"
                            size="small"
                            error={!!errors.resaleNumber}
                            helperText={(errors.resaleNumber as any)?.message}
                            {...register("resaleNumber")}
                            onChange={(e) => setValue("resaleNumber", e.target?.value, { shouldValidate: true })}
                            value={getValues("resaleNumber") || ""}
                        /> 
                    </Grid>
                    <Grid item lg={6} sm={12} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Resale Date Established"
                            variant="outlined"
                            type="datetime-local"
                            size="small"
                            className="datetime-picker"
                            error={!!errors.resaleDateEstablished}
                            helperText={(errors.resaleDateEstablished as any)?.message}
                            {...register("resaleDateEstablished")}
                            onChange={(e) => setValue("resaleDateEstablished", e.target?.value, { shouldValidate: true })}
                            value={getValues("resaleDateEstablished") || ""}
                        /> 
                    </Grid>
                </Grid>
                <InputSeparator title="Address"/>
                <Grid container rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} lg={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Street Address"
                            variant="outlined"
                            size="small"
                            error={!!errors.streetAddress}
                            helperText={(errors.streetAddress as any)?.message}
                            {...register("streetAddress", {
                                required: "Field is required"
                            })}
                            onChange={(e) => setValue("streetAddress", e.target?.value, { shouldValidate: true })}
                            required
                            value={getValues("streetAddress") || ""}
                        /> 
                    </Grid>
                    <Grid item sm={12} lg={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="City"
                            variant="outlined"
                            size="small"
                            error={!!errors.city}
                            helperText={(errors.city as any)?.message}
                            {...register("city", {
                                required: "Field is required"
                            })}
                            onChange={(e) => setValue("city", e.target?.value, { shouldValidate: true })}
                            required
                            value={getValues("city") || ""}
                        />                          
                    </Grid>
                    <Grid item sm={12} lg={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Zip"
                            variant="outlined"
                            size="small"
                            error={!!errors.zip}
                            helperText={(errors.zip as any)?.message}
                            {...register("zip", {
                                required: "Field is required"
                            })}
                            onChange={(e) => setValue("zip", e.target?.value, { shouldValidate: true })}
                            required
                            value={getValues("zip") || ""}
                        />
                    </Grid>
                    <Grid item sm={12} lg={6} sx={{marginBottom: "18px"}}>
                        <AddressFormAutocomplete
                            label={"Country"}
                            error={!!errors.country}
                            helperText={(errors.country as any)?.message}
                            {...register("country", {
                                required: "Field is required"
                            })}
                            onChange={(e, value) =>
                                value === null ? "" : setValue("country", value.id, { shouldValidate: true })
                            }
                            options={CountryEnum}
                            size="small"
                            required
                            value={getValues("country") || ""}
                        />
                    </Grid>
                    <Grid item sm={12} lg={6} sx={{marginBottom: "18px"}}>
                        { country as string == 'US' ?
                            <AddressFormAutocomplete
                                error={!!errors.state}
                                helperText={(errors.state as any)?.message}
                                {...register("state")}
                                label={"State"}
                                onChange={(e, value) =>
                                value === null ? "" : setValue("state", value.id, { shouldValidate: true })
                                }
                                size="small"
                                options={StateEnum}
                                value={getValues("state") || ""}
                            />
                        :
                            <TextField
                                label="State / Region"
                                variant="outlined"
                                size="small"
                                error={!!errors.state}
                                helperText={(errors.state as any)?.message}
                                {...register("state", {
                                required: false
                                })}
                                onChange={(e) => setValue("state", e.target?.value, { shouldValidate: true })}
                                value={getValues("state") || ""}
                            />
                        }
                    </Grid>
                </Grid> 
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Create
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    );
};
